/*
 * File: ContactList.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { GetStudentResponse } from 'types/students';

import ContactItem from './ContactItem';
import ContactSection from './ContactSection';
import { getContactSummary } from './getContactSummary';

interface ContactListProps {
  /**
   * Student user data containing phones and email
   */
  student: GetStudentResponse | null | undefined;

  /**
   * Array of parent/guardian contacts
   */
  parents: GetStudentResponse['student']['parents'] | undefined;
}

/**
 * Displays all contact information for a student and their parents/guardians
 * Encapsulates the complex conditional rendering logic for contacts
 */
export default function ContactList({ student, parents }: ContactListProps) {
  const hasStudentContacts = (student?.user_phones.length ?? 0) > 0 || !!student?.email;
  const hasParentContacts = (parents?.length ?? 0) > 0;
  const hasAnyContacts = hasStudentContacts || hasParentContacts;

  if (!hasAnyContacts || !student) {
    return null;
  }

  const summary = getContactSummary(student, parents ?? []);

  return (
    <Accordion defaultExpanded={false} elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 0,
          minHeight: 'auto',
          '&.Mui-expanded': { minHeight: 'auto' },
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Contact Information
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {summary}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Stack gap={2}>
          {/* Student Section */}
          {hasStudentContacts && (
            <ContactSection label="Student">
              {student.user_phones?.map((p, i) => (
                <ContactItem key={i} type="phone" value={p.phone} />
              ))}
              {student.email && <ContactItem type="email" value={student.email} />}
            </ContactSection>
          )}

          {/* Parents */}
          {parents?.map((parent, i) => (
            <ContactSection
              key={i}
              label={`${parent.first_name} ${parent.last_name}${parent.relation ? ` (${parent.relation})` : ''}`}
            >
              {parent.cell_phone && <ContactItem type="phone" label="Cell" value={parent.cell_phone} />}
              {parent.home_phone && <ContactItem type="phone" label="Home" value={parent.home_phone} />}
              {parent.work_phone && (
                <ContactItem type="phone" label="Work" value={parent.work_phone} extension={parent.work_extension} />
              )}
              {parent.email && <ContactItem type="email" label="Email" value={parent.email} />}
            </ContactSection>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
